import React, { useState, useRef, useEffect } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { Tooltip } from 'react-tooltip';
import apiHelper from '../../utils/apiHelpers';
import { linkifyCardNames, cardNameFromUrl } from '../../utils/chatCardLinks';
import CardHoverPreview from '../card/CardHoverPreview';
import Button from '../common/Button';
import Alert from '../common/Alert';
import ConfirmDialog from '../common/ConfirmDialog';

const CARD_TOOLTIP_ID = 'chat-card-preview';

// Tables, strikethrough and task lists are GitHub-flavoured markdown, which
// react-markdown does not parse on its own — without this a table renders as
// its raw pipes and dashes.
const MARKDOWN_PLUGINS = [remarkGfm];

const MARKDOWN_COMPONENTS = {
  // Card names the assistant marks up become links into our own card search.
  // Those links also preview the card's art on hover; any other URL the
  // assistant writes is left as a plain link.
  a: ({ href, children }) => {
    const cardName = cardNameFromUrl(href);
    return (
      <a
        href={href}
        target="_blank"
        rel="noopener noreferrer"
        className="underline text-accentLight hover:text-white"
        data-tooltip-id={cardName ? CARD_TOOLTIP_ID : undefined}
        data-card-name={cardName || undefined}>
        {children}
      </a>
    );
  },
  // A three-column "swap this for that" table is wider than the chat bubble, so
  // let the table scroll instead of overflowing the message.
  table: ({ children }) => (
    <div className="overflow-x-auto">
      <table className="w-full text-left">{children}</table>
    </div>
  ),
  // `style` carries the column alignment from the table's `:---` row, so it has
  // to be forwarded rather than dropped.
  th: ({ children, style }) => (
    <th style={style} className="px-2 py-1 border border-gray-500 align-top">
      {children}
    </th>
  ),
  td: ({ children, style }) => (
    <td style={style} className="px-2 py-1 border border-gray-500 align-top">
      {children}
    </td>
  ),
};

const TypingIndicator = () => (
  <div className="flex gap-1 items-center px-4 py-3 bg-mediumColor rounded-md w-fit">
    <span className="w-2 h-2 bg-gray-300 rounded-full animate-bounce [animation-delay:-0.3s]"></span>
    <span className="w-2 h-2 bg-gray-300 rounded-full animate-bounce [animation-delay:-0.15s]"></span>
    <span className="w-2 h-2 bg-gray-300 rounded-full animate-bounce"></span>
  </div>
);

const ChatAdvisor = ({
  format,
  commanderName,
  deckListText,
  deckDescription,
  messages = [],
  onMessagesChange,
}) => {
  const [chatInput, setChatInput] = useState('');
  const [sending, setSending] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [showClearConfirm, setShowClearConfirm] = useState(false);
  const messagesEndRef = useRef(null);
  // Focused after a failed send, so the restored message is ready to edit.
  const chatInputRef = useRef(null);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, sending]);

  const handleSend = () => {
    const content = chatInput.trim();
    if (!content || sending) return;

    const newMessages = [...messages, { role: 'user', content }];
    onMessagesChange(newMessages);
    setChatInput('');
    setSending(true);
    setErrorMessage('');

    apiHelper
      .sendChatMessage({
        messages: newMessages,
        format,
        commanderName,
        deckListText,
        deckDescription,
      })
      .then(response => {
        // Functional form: the setter belongs to the parent, so this lands even
        // if the user switched tabs while the reply was in flight.
        onMessagesChange(prev => [
          ...prev,
          {
            role: 'model',
            content: response.data.reply,
            // Which model answered — the reply rotates through the configured
            // list as each one's daily quota runs out.
            model: response.data.model,
          },
        ]);
        setSending(false);
      })
      .catch(error => {
        setSending(false);

        // A failed send never reached the model, so the message is rolled back
        // out of the transcript and put back in the textarea. Leaving it in
        // history would both misrepresent the conversation — the next send would
        // replay it as context the assistant never answered — and force the user
        // to retype it, ending up with it twice.
        onMessagesChange(prev => {
          const last = prev[prev.length - 1];
          return last && last.role === 'user' && last.content === content
            ? prev.slice(0, -1)
            : prev;
        });
        // The textarea is disabled while sending, so it is empty here; the guard
        // is only so a draft could never be overwritten by the rollback.
        setChatInput(current => current || content);
        chatInputRef.current?.focus();

        let message;
        if (error.response && error.response.data?.message) {
          message = error.response.data.message;
        } else if (error.request) {
          message = 'Sin respuesta del Servidor';
        } else {
          message = 'Error de conexión con el servidor';
        }
        setErrorMessage(
          `${message} Tu mensaje volvió al cuadro de texto: podés editarlo y reenviarlo.`,
        );
      });
  };

  const handleKeyDown = e => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  // Clears the conversation only — the draft in the textarea isn't part of it.
  const handleClearChat = () => {
    onMessagesChange([]);
    setErrorMessage('');
    setShowClearConfirm(false);
  };

  return (
    <div className="flex flex-col gap-y-3 h-[70vh] sm:h-[32rem]">
      <div className="flex-1 min-h-0 overflow-y-auto flex flex-col gap-y-3 p-3 bg-lightColor rounded-md">
        {messages.length === 0 && !sending && (
          <p className="text-gray-200 text-center m-auto px-4">
            Preguntame lo que quieras sobre cómo armar o mejorar tu mazo de
            Commander. Por ejemplo: "¿qué le falta a mi mazo?" o "¿qué combos
            tiene mi comandante?".
          </p>
        )}
        {messages.map((message, index) => (
          <div
            key={index}
            className={`max-w-[85%] sm:max-w-[75%] rounded-md px-4 py-2 ${
              message.role === 'user'
                ? 'self-end bg-accentDark text-white'
                : 'self-start bg-mediumColor text-white'
            }`}>
            {message.role === 'model' ? (
              <>
                <div className="prose prose-invert prose-sm max-w-none">
                  <ReactMarkdown
                    remarkPlugins={MARKDOWN_PLUGINS}
                    components={MARKDOWN_COMPONENTS}>
                    {linkifyCardNames(message.content)}
                  </ReactMarkdown>
                </div>
                {message.model && (
                  <p className="text-xs italic text-gray-300 mt-1">
                    Generado con {message.model}
                  </p>
                )}
              </>
            ) : (
              <p className="whitespace-pre-wrap">{message.content}</p>
            )}
          </div>
        ))}
        {sending && (
          <div className="self-start">
            <TypingIndicator />
          </div>
        )}
        <div ref={messagesEndRef} />
      </div>

      {/* One tooltip for every card link: it reads which link is hovered and
          renders that card's art. Kept outside the scrolling message list so it
          isn't clipped by the overflow. */}
      <Tooltip
        id={CARD_TOOLTIP_ID}
        place="right"
        className="!p-1 !bg-darkColor !opacity-100 z-50"
        render={({ activeAnchor }) => {
          const name = activeAnchor?.getAttribute('data-card-name');
          return name ? <CardHoverPreview name={name} /> : null;
        }}
      />

      {errorMessage && <Alert>{errorMessage}</Alert>}

      <div className="flex flex-col sm:flex-row gap-2">
        <textarea
          ref={chatInputRef}
          className="block w-full h-auto px-3 py-1.5 text-base font-normal leading-6 placeholder-gray-500 text-gray-600 bg-white border border-gray-300 rounded-md transition-colors duration-150 ease-in-out resize-none disabled:bg-lightColor disabled:text-gray-300"
          rows={2}
          placeholder="Escribí tu pregunta sobre el mazo..."
          value={chatInput}
          onChange={e => setChatInput(e.target.value)}
          onKeyDown={handleKeyDown}
          disabled={sending}
        />
        <div className="flex flex-col gap-2 w-full sm:w-32 shrink-0">
          <Button
            className="w-full"
            iconClass="fas fa-paper-plane"
            onClick={handleSend}
            disabled={sending || !chatInput.trim()}>
            Enviar
          </Button>
          <Button
            className="w-full"
            iconClass="fas fa-eraser"
            onClick={() => setShowClearConfirm(true)}
            disabled={sending || !messages.length}>
            Limpiar
          </Button>
        </div>
      </div>

      <ConfirmDialog
        open={showClearConfirm}
        title="Limpiar la conversación"
        message="Se va a borrar toda la conversación con el asistente. Tu lista de cartas y la descripción del mazo se mantienen."
        confirmLabel="Limpiar"
        confirmIconClass="fas fa-eraser"
        onConfirm={handleClearChat}
        onClose={() => setShowClearConfirm(false)}
      />
    </div>
  );
};

export default ChatAdvisor;
